import { useState, useEffect, FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../lib/api';
import { useAuth, AuthUser } from '../lib/auth';

type Tab = 'password' | 'magic';

function extractInviteToken(redirect: string | null): string | null {
  if (!redirect) return null;
  try {
    const params = new URLSearchParams(redirect.split('?')[1] ?? '');
    return redirect.includes('accept-invite') ? params.get('token') : null;
  } catch { return null; }
}

export default function LoginPage() {
  const { login } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirect = searchParams.get('redirect');
  const inviteToken = extractInviteToken(redirect);

  const [tab, setTab] = useState<Tab>('password');
  const [inviteCompany, setInviteCompany] = useState<string | null>(null);

  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [error, setError]       = useState('');
  const [loading, setLoading]   = useState(false);
  const [unverified, setUnverified] = useState(false);

  const [magicEmail, setMagicEmail] = useState('');
  const [magicSent, setMagicSent]   = useState(false);
  const [magicLoading, setMagicLoading] = useState(false);
  const [magicError, setMagicError] = useState('');

  useEffect(() => {
    if (!inviteToken) return;
    api.get<{ valid: boolean; companyName?: string }>(`/companies/invite-info?token=${inviteToken}`)
      .then(r => { if (r.valid && r.companyName) setInviteCompany(r.companyName); })
      .catch(() => {});
  }, [inviteToken]);

  async function handlePasswordSubmit(e: FormEvent) {
    e.preventDefault();
    setError(''); setUnverified(false); setLoading(true);
    try {
      const res = await api.post<{ token: string; user: AuthUser }>('/auth/login', { email, password });
      login(res.token, res.user);
      navigate(redirect ?? (res.user.role === 'admin' ? '/admin' : '/'));
    } catch (err: any) {
      if (err.message?.includes('verify your email') || err.emailNotVerified) {
        setUnverified(true);
        setError(err.message ?? 'Please verify your email before signing in.');
      } else {
        setError(err.message ?? 'Login failed.');
      }
    } finally { setLoading(false); }
  }

  async function handleMagicSubmit(e: FormEvent) {
    e.preventDefault();
    setMagicError(''); setMagicLoading(true);
    try {
      await api.post('/auth/magic-link', { email: magicEmail });
      setMagicSent(true);
    } catch (err: any) {
      setMagicError(err.message ?? 'Failed to send link.');
    } finally { setMagicLoading(false); }
  }

  const registerHref = `/register${redirect ? `?redirect=${encodeURIComponent(redirect)}` : ''}`;

  return (
    <div className="min-h-screen flex" style={{ background: '#f4f4f4' }}>

      {/* ── Left panel ── */}
      <div className="hidden lg:flex flex-col justify-between w-1/2 px-16 py-12"
        style={{ background: '#f4f4f4', borderRight: '1px solid #e5e5e5' }}>
        <img src="https://cdn.shopify.com/s/files/1/0774/8055/8750/files/MontiPower_Same_Font_Copyright_Original_Black_Red_1200px.png?v=1787509899"
          alt="MONTIPOWER" style={{ height: 28, width: 'auto', objectFit: 'contain', objectPosition: 'left' }} />
        <div>
          <p className="text-sm font-semibold uppercase tracking-widest mb-4" style={{ color: '#e00000' }}>Warranty Portal</p>
          <h1 className="text-4xl font-bold leading-tight mb-6" style={{ color: '#0b0d0e', fontFamily: "'Archivo', sans-serif" }}>
            Register your tools.<br />Track your warranty.<br />Get support fast.
          </h1>
          <p style={{ color: '#8f9b9b', fontSize: 15, lineHeight: 1.7 }}>
            One place to manage all your Montipower equipment — from registration to warranty claims and consumable reorders.
          </p>
        </div>
        <p style={{ fontSize: 12, color: '#8f9b9b' }}>© {new Date().getFullYear()} MONTI Surface Tech Group B.V. · All rights reserved.</p>
      </div>

      {/* ── Right panel ── */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-12">
        <div className="lg:hidden mb-8 text-center">
          <img src="https://cdn.shopify.com/s/files/1/0774/8055/8750/files/MontiPower_Same_Font_Copyright_Original_Black_Red_1200px.png?v=1787509899"
            alt="MONTIPOWER" style={{ height: 24, width: 'auto', margin: '0 auto 6px' }} />
          <p style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.14em', textTransform: 'uppercase', color: '#aaa' }}>Warranty Portal</p>
        </div>

        <div className="w-full max-w-sm">

          {/* Invite context banner */}
          {inviteCompany && (
            <div className="mb-5 px-4 py-3 rounded-lg flex items-center gap-3" style={{ background: '#fff', border: '1px solid #e5e5e5' }}>
              <div className="w-8 h-8 rounded flex items-center justify-center flex-shrink-0" style={{ background: '#fff0f0' }}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#e00000" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 21h18M3 7l9-4 9 4M4 7v14M20 7v14M9 21V11h6v10"/>
                </svg>
              </div>
              <p className="text-sm" style={{ color: '#374151' }}>
                You've been invited to join <strong style={{ color: '#0b0d0e' }}>{inviteCompany}</strong>. Sign in to accept.
              </p>
            </div>
          )}

          <h2 className="text-2xl font-bold mb-1" style={{ color: '#0b0d0e' }}>{t('auth.signIn')}</h2>
          <p className="text-sm mb-6" style={{ color: '#8f9b9b' }}>{t('auth.signInSubtitle')}</p>

          {/* Tabs */}
          <div className="flex mb-6 rounded-lg overflow-hidden" style={{ border: '1px solid #e5e5e5', background: '#f4f4f4' }}>
            {([['password', 'Password'], ['magic', 'Email link']] as [Tab, string][]).map(([t2, label]) => (
              <button key={t2} onClick={() => { setTab(t2); setError(''); setMagicError(''); }}
                className="flex-1 py-2.5 text-sm font-medium"
                style={{ background: tab === t2 ? '#fff' : 'transparent', color: tab === t2 ? '#0b0d0e' : '#8f9b9b', border: 'none', borderRadius: 6, margin: 3, cursor: 'pointer' }}>
                {label}
              </button>
            ))}
          </div>

          {tab === 'password' && (
            <>
              {error && (
                <div className="mb-4" style={{ padding: '12px 14px', borderRadius: 6, background: unverified ? '#fff7ed' : '#fef2f2', border: `1px solid ${unverified ? '#fed7aa' : '#fca5a5'}`, color: unverified ? '#9a3412' : '#991b1b', fontSize: 13 }}>
                  {error}
                  {unverified && (
                    <div className="mt-2">
                      <button onClick={() => navigate('/verify-pending', { state: { email } })} className="text-xs font-semibold underline">
                        Resend verification email →
                      </button>
                    </div>
                  )}
                </div>
              )}
              <form onSubmit={handlePasswordSubmit} className="space-y-4">
                <div>
                  <label className="label">{t('auth.email')}</label>
                  <input className="input" type="email" required autoComplete="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com" />
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="label" style={{ margin: 0 }}>{t('auth.password')}</label>
                    <Link to="/forgot-password" style={{ fontSize: 11, color: '#8f9b9b' }}>{t('auth.forgotPassword')}</Link>
                  </div>
                  <input className="input" type="password" required autoComplete="current-password" value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••" />
                </div>
                <button className="btn-primary w-full" type="submit" disabled={loading}>{loading ? t('auth.signingIn') : t('auth.signIn')}</button>
              </form>
            </>
          )}

          {tab === 'magic' && (
            magicSent ? (
              <div className="text-center py-4">
                <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: '#fff0f0', border: '2px solid #fca5a5' }}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#e00000" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/>
                  </svg>
                </div>
                <p className="font-semibold mb-1" style={{ color: '#0b0d0e' }}>Check your inbox</p>
                <p className="text-sm" style={{ color: '#8f9b9b', lineHeight: 1.7 }}>
                  We sent a sign-in link to <strong style={{ color: '#374151' }}>{magicEmail}</strong>. It expires in 15 minutes.
                </p>
                <button onClick={() => { setMagicSent(false); setMagicEmail(''); }} className="mt-4 text-sm" style={{ color: '#8f9b9b', background: 'none', border: 'none', cursor: 'pointer' }}>
                  Use a different email
                </button>
              </div>
            ) : (
              <>
                <p className="text-sm mb-4" style={{ color: '#8f9b9b' }}>Enter your email and we'll send a one-click sign-in link — no password needed.</p>
                {magicError && <div className="alert-error mb-4">{magicError}</div>}
                <form onSubmit={handleMagicSubmit} className="space-y-4">
                  <div>
                    <label className="label">{t('auth.email')}</label>
                    <input className="input" type="email" required autoComplete="email" value={magicEmail} onChange={e => setMagicEmail(e.target.value)} placeholder="you@example.com" />
                  </div>
                  <button className="btn-primary w-full" type="submit" disabled={magicLoading}>{magicLoading ? t('auth.sending') : 'Send sign-in link'}</button>
                </form>
              </>
            )
          )}

          <p className="mt-6 text-sm text-center" style={{ color: '#8f9b9b' }}>
            {t('auth.noAccount')}{' '}
            <Link to={registerHref} style={{ color: '#e00000', fontWeight: 600 }}>{t('auth.register')}</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
